import { Component, OnInit, signal, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { empty } from "../../functions";
import { AlertService } from "../../shared/services/alert.service";
import { StudentProfileService } from "../../api/services/student-profile.service";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { catchError, first } from "rxjs/operators";
import { of } from "rxjs";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";

@Component({
  selector: "app-modal-company-apply",
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  template: `
    <div class="modal-content">
      <div class="modal-header border-bottom">
        <h5 class="modal-title font-weight-bold">
          {{ "Apply to company" | translate }}
        </h5>
        <button
          tabindex="10"
          type="button"
          class="close"
          (click)="activeModal.close(false)"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-body p-4">
        <!-- Privacy Notice -->
        <div class="alert alert-info mb-4" role="alert">
          <div class="d-flex align-items-start">
            <i class="fas fa-info-circle mt-1 mr-2"></i>
            <div class="small">
              {{
                "Your CV will be shared via a public link. By applying, you consent to this for employment opportunities."
                  | translate
              }}
            </div>
          </div>
        </div>

        <div class="form-group mb-4">
          <h6 class="font-weight-bold text-primary">
            {{ "Company" | translate }}
          </h6>
          <p class="text-muted mb-0">{{ company().name }}</p>
        </div>

        <div class="form-group mb-4">
          <label for="motivationText" class="font-weight-bold">{{
            "Motivation / Introduction" | translate
          }}</label>
          <p class="text-muted small mb-2">
            {{
              "Write a personal introduction or motivation to the company. Explain why you are interested in working with them."
                | translate
            }}
          </p>
          <textarea
            id="motivationText"
            rows="8"
            class="form-control"
            [value]="motivationText()"
            (input)="motivationText.set($any($event.target).value)"
            [placeholder]="
              'Tell the company about yourself and why you would like to work with them...'
                | translate
            "
          >
          </textarea>
        </div>
      </div>

      <div class="modal-footer border-top">
        <div class="d-flex justify-content-between w-100">
          <button
            type="button"
            class="btn btn-outline-secondary"
            (click)="activeModal.close(false)"
            [disabled]="isUploading()"
          >
            {{ "Cancel" | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            (click)="Apply()"
            [disabled]="isFormInvalid() || isUploading()"
          >
            <span
              *ngIf="isUploading()"
              class="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
            >
            </span>
            {{
              isUploading()
                ? ("Sending..." | translate)
                : ("Send Application" | translate)
            }}
          </button>
        </div>
      </div>
    </div>
  `,
})
export class ModalCompanyApplyComponent implements OnInit {
  public data: { company: { id: string; name: string } } | undefined;

  // Signals for reactive state management
  company = signal<{ id: string; name: string }>({ id: "", name: "" });
  motivationText = signal<string>("");
  isUploading = signal<boolean>(false);

  // Computed signal for form validation
  isFormInvalid = computed(() => {
    return empty(this.company().id) || empty(this.motivationText());
  });

  constructor(
    public activeModal: NgbActiveModal,
    private alertService: AlertService,
    private studentProfileService: StudentProfileService,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    if (this.data && "company" in this.data) {
      this.company.set(this.data.company);
    }
  }

  /**
   * Apply to company with motivation text
   */
  Apply(): void {
    if (this.isFormInvalid()) {
      return;
    }

    this.isUploading.set(true);

    this.sendCompanyApplication();
  }

  /**
   * Send company application using the CV API
   */
  private sendCompanyApplication(): void {
    // Get current student profile (use first() to avoid subscription issues)
    this.authService.studentprofile$
      .pipe(first())
      .subscribe((studentProfile) => {
        if (!studentProfile?.id) {
          this.isUploading.set(false);
          this.alertService.error(
            marker(
              "Student profile not found. Please make sure you are logged in."
            )
          );
          return;
        }

        // Prepare the data for sending CV to company
        const cvData = {
          companyToSendTo: this.company().id,
          message: this.motivationText(),
        };

        // Call the API to send CV to company
        this.studentProfileService
          .sendCvToCompany({
            studentId: studentProfile.id,
            body: cvData,
          })
          .pipe(
            catchError((error) => {
              this.isUploading.set(false);
              this.alertService.error(
                marker(
                  "There was an error sending your application. Please make sure you have completed your match test and your CV has been generated, then try again."
                )
              );
              console.error("Error sending CV to company:", error);
              return of(null); // Return observable to handle error gracefully
            })
          )
          .subscribe((result) => {
            if (result !== null) {
              this.isUploading.set(false);
              this.activeModal.close(true);
              this.alertService.success(
                marker(
                  "Your D.N.AI CV has been successfully sent to the company!"
                )
              );
            }
          });
      });
  }
}
